// R-131-FIN-DECISION-D — queue-time + construction / CAPEX overrun stochastics.

import {
  buildPathwayCashflows,
  derivePathwayTimeline,
  type PathwayCashflow,
} from './pathwayCashflows';
import { computePathwayLcoe } from './pathwayLcoe';
import { pathwayDiscountRateFraction } from './pathwayCashflows';
import { blendPhaseEconomics } from './presets/shared';
import type { Pathway, TechnologyMaturity } from './types';

export interface MonteCarloOpts {
  runs: number;
  seedRng?: number;
  /** Analysis anchor date — defaults to UTC year of first construction phase. */
  now?: Date;
  sample: {
    queueYears?: boolean;
    constructionMonths?: boolean;
    capexUsd?: boolean;
    fuelPriceCagr?: boolean;
    availabilityPct?: boolean;
  };
}

export interface MonteCarloRunMetrics {
  /**
   * REAL LCOE, matching the central value every consumer prints beside this
   * band (`PathwayCompareMetrics.lcoeRealUsdPerMwh`).
   *
   * C5 — this used to sample the NOMINAL LCOE while the central value was REAL,
   * so `PathwayBandChart` drew a P10–P90 band that did not contain its own
   * central marker for nuclear-large, solar and grid. Nominal and real differ
   * per TECHNOLOGY (the escalation profiles differ), so the gap was not a
   * constant offset a reader could mentally correct for. Named for the basis so
   * a future edit cannot re-cross the two without saying so.
   */
  lcoeRealPerMwhUsd: number;
  npvUsd: number;
  irrPct: number;
  codYear: number;
}

export interface MonteCarloPercentileSummary {
  p10: number;
  median: number;
  p90: number;
}

export interface MonteCarloResult {
  perRun: MonteCarloRunMetrics[];
  summary: {
    lcoe: MonteCarloPercentileSummary;
    npv: MonteCarloPercentileSummary;
    irr: MonteCarloPercentileSummary;
    codYear: MonteCarloPercentileSummary;
    pPositiveNpv: number;
    /**
     * Number of Monte Carlo samples whose IRR did not converge (no sign change
     * in the cashflows, or neither Newton-Raphson nor bisection bracketed a
     * root). These samples carry `irrPct = NaN` in `perRun` and are EXCLUDED
     * from the IRR percentile summary above so the percentiles are not silently
     * dragged toward 0. Surfaced here so non-convergence is visible to callers.
     * If `irrNonConvergedCount === runs`, the IRR p10/median/p90 are all NaN.
     */
    irrNonConvergedCount: number;
    /** Fraction of samples (0..1) whose IRR did not converge. */
    irrNonConvergedFraction: number;
  };
}

/** Default MC settings for pathway comparison cards. */
export const DEFAULT_COMPARE_MONTE_CARLO_OPTS: MonteCarloOpts = {
  runs: 1000,
  sample: {
    queueYears: true,
    constructionMonths: true,
    capexUsd: true,
    fuelPriceCagr: true,
    availabilityPct: true,
  },
};

/** Mulberry32 — fast seeded PRNG for reproducible Monte Carlo runs. */
export function createSeededRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalStandard(rng: () => number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function normalMeanStdev(mean: number, stdev: number, rng: () => number): number {
  return mean + stdev * normalStandard(rng);
}

function logNormalMeanStdev(mean: number, stdev: number, rng: () => number): number {
  if (mean <= 0) return 0;
  const variance = stdev * stdev;
  const mu = Math.log((mean * mean) / Math.sqrt(variance + mean * mean));
  const sigma = Math.sqrt(Math.log(1 + variance / (mean * mean)));
  return Math.exp(mu + sigma * normalStandard(rng));
}

function sampleTriangular(min: number, mode: number, max: number, rng: () => number): number {
  if (min >= max) return mode;
  const lo = Math.min(min, mode, max);
  const hi = Math.max(min, mode, max);
  const peak = clamp(mode, lo, hi);
  const u = rng();
  const fc = (peak - lo) / (hi - lo);
  if (u < fc) {
    return lo + Math.sqrt(u * (hi - lo) * (peak - lo));
  }
  return hi - Math.sqrt((1 - u) * (hi - lo) * (hi - peak));
}

function sampleGamma(shape: number, rng: () => number): number {
  if (shape <= 0) return 0;
  if (shape < 1) {
    return sampleGamma(shape + 1, rng) * Math.pow(rng(), 1 / shape);
  }
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  while (true) {
    let x = 0;
    let v = 0;
    do {
      x = normalStandard(rng);
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = rng();
    if (u < 1 - 0.0331 * x * x * x * x) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

function sampleBeta(alpha: number, beta: number, rng: () => number): number {
  const x = sampleGamma(alpha, rng);
  const y = sampleGamma(beta, rng);
  const sum = x + y;
  return sum > 0 ? x / sum : 0.5;
}

function availabilityBetaParams(meanPct: number): { alpha: number; beta: number } {
  const mean = clamp(meanPct / 100, 0.01, 0.99);
  const concentration = 80;
  return { alpha: mean * concentration, beta: (1 - mean) * concentration };
}

/**
 * Bound a sampled availability RELATIVE to the pathway's stated value.
 *
 * `revenue.availabilityPct` is POLYSEMOUS across the presets: the thermal ones
 * put a true availability there (gas-cc 92, SMR 95), while solar and wind put a
 * CAPACITY FACTOR (solar 30). The draw used to be clamped to an absolute
 * [50, 100] band, which is only meaningful under the first reading — every
 * solar sample was forced up to the 50 floor, i.e. from a 30% capacity factor
 * to 50%, inflating its delivered energy by two thirds and cutting its LCOE by
 * roughly 40%.
 *
 * That was harmless only for as long as the availability draw was inert (C7);
 * making the draw bite made the clamp bite too. The Beta above is already
 * centred on the stated value and tightly concentrated, so the clamp is only a
 * guard against a pathological tail — and a guard has to be expressed in terms
 * of the value it is guarding, not a magnitude assumed for one reading of an
 * ambiguous field.
 */
function clampSampledAvailabilityPct(sampledPct: number, statedPct: number): number {
  if (!Number.isFinite(statedPct) || statedPct <= 0) return clamp(sampledPct, 0, 100);
  return clamp(sampledPct, statedPct * 0.5, Math.min(100, statedPct * 1.15));
}

function capexOverrunStdevFraction(maturity: TechnologyMaturity): number {
  switch (maturity) {
    case 'first-of-kind':
      return 0.275;
    case 'commercial-but-evolving':
      return 0.2;
    case 'mature':
    default:
      return 0.125;
  }
}

function totalConstructionMonths(pathway: Pathway): number {
  return pathway.phases
    .filter((phase) => phase.kind === 'construction')
    .reduce((sum, phase) => sum + phase.durationMonths, 0);
}

function clonePathway(pathway: Pathway): Pathway {
  return structuredClone(pathway);
}

function scalePathwayCapex(pathway: Pathway, multiplier: number): void {
  if (multiplier === 1) return;
  pathway.capex.baseUsd *= multiplier;
  for (const phase of pathway.phases) {
    if (phase.kind !== 'construction') continue;
    if (phase.capexUsd != null) {
      phase.capexUsd *= multiplier;
    } else if (phase.sourceMix && phase.sourceMix.length > 0) {
      // Mixed-source construction phase: capex is derived from the source mix at
      // cashflow time, so there is no phase.capexUsd to scale. Materialize the
      // scaled blended GENERATION capex onto the (cloned) phase so the CAPEX-overrun
      // draw applies to it too. Shared interconnect lives on pathway.capex and is
      // scaled separately below — calling blendPhaseEconomics without a shared total
      // (default 0) matches the cashflow builder's phaseCapexUsd path, so no double-count.
      phase.capexUsd = blendPhaseEconomics(phase.sourceMix).totalCapexUsd * multiplier;
    }
  }
  if (pathway.capex.substationUsd != null) pathway.capex.substationUsd *= multiplier;
  if (pathway.capex.transmissionUpgradeUsd != null) {
    pathway.capex.transmissionUpgradeUsd *= multiplier;
  }
  if (pathway.capex.fuelLogisticsUsd != null) pathway.capex.fuelLogisticsUsd *= multiplier;
}

function setConstructionMonths(pathway: Pathway, months: number): void {
  const rounded = Math.max(1, Math.round(months));
  for (const phase of pathway.phases) {
    if (phase.kind === 'construction') {
      phase.durationMonths = rounded;
    }
  }
}

/**
 * Apply a sampled availability to a pathway.
 *
 * C7 — this used to set `revenue.availabilityPct` and stop. Every preset writes
 * an explicit `phase.annualMwh`, and both `nameplateAnnualMwh` and
 * `annualRevenueUsd` short-circuit before `availabilityPct` precisely so a
 * CF-adjusted energy figure is not derated twice. The field therefore had no
 * reader on any preset pathway, and the availability draw moved NOTHING while
 * the Monte Carlo advertised it as a sampled uncertainty — the P10/P90 bands
 * were narrower than the stated model.
 *
 * The draw now acts where availability actually lives: on DELIVERED ENERGY,
 * scaled RELATIVE to the pathway's stated availability, which is the basis the
 * preset's `annualMwh` already embeds. Scaling by the ratio keeps that
 * single-derate invariant intact — at the stated availability the ratio is
 * exactly 1 and the pathway is unchanged.
 *
 * With no stated availability to scale from there is no defensible ratio, so
 * the energy is left alone rather than derated from an assumed 100%.
 */
function applySampledAvailability(pathway: Pathway, availabilityPct: number): void {
  const stated = pathway.revenue.availabilityPct;
  pathway.revenue.availabilityPct = availabilityPct;
  if (!Number.isFinite(stated) || stated <= 0) return;
  const ratio = availabilityPct / stated;
  if (!Number.isFinite(ratio) || ratio <= 0) return;

  for (const phase of pathway.phases) {
    if (phase.kind !== 'operation') continue;
    if (phase.annualMwh != null && Number.isFinite(phase.annualMwh)) {
      phase.annualMwh *= ratio;
    }
    // A mixed-source phase derives its energy from the mix at cashflow time and
    // carries no `annualMwh` to scale, and the mix components hold capacity
    // (`fractionMw`), not energy. Materialise the blended energy at the sampled
    // availability onto the cloned phase — the same technique the CAPEX draw
    // uses for `phase.capexUsd`, and `phaseAnnualMwh` prefers the explicit
    // field, so the blend is consumed exactly once.
    else if (phase.sourceMix && phase.sourceMix.length > 0) {
      const blended = blendPhaseEconomics(phase.sourceMix).annualMwh;
      if (Number.isFinite(blended) && blended > 0) phase.annualMwh = blended * ratio;
    }
  }
  // A simulated yield outranks the preset energy in `nameplateAnnualMwh`, so it
  // must be derated too or the draw is a no-op again on exactly the pathways
  // carrying the best evidence.
  if (pathway.simYield?.annualMwh != null && Number.isFinite(pathway.simYield.annualMwh)) {
    pathway.simYield = { ...pathway.simYield, annualMwh: pathway.simYield.annualMwh * ratio };
  }
}

function samplePathway(
  pathway: Pathway,
  sample: MonteCarloOpts['sample'],
  rng: () => number,
): Pathway {
  const p = clonePathway(pathway);

  // C7 — QUEUE AND CONSTRUCTION ARE THE SAME QUANTITY ON A GRID PATHWAY. The
  // grid preset's construction phase IS the interconnection wait
  // (`constructionMonths = PLACEHOLDER_QUEUE_YEARS.medianYears * 12`), so both
  // draws write the same field. Both are on by default, and the construction
  // draw ran second off the ORIGINAL pathway — so it overwrote the queue draw
  // on every single run, and the ISO-sourced triangular queue distribution
  // never reached a single percentile. Applying both would be worse still: it
  // would compound two uncertainties over one quantity.
  //
  // The queue draw wins where it applies, because it is the SPECIFIC evidence
  // (an asymmetric, ISO-sourced triangular distribution) and the construction
  // draw is a generic ±15% lognormal around the stated duration.
  let queueDrawApplied = false;
  if (
    sample.queueYears &&
    p.classification === 'grid' &&
    p.risk.queueDelayDistribution
  ) {
    const dist = p.risk.queueDelayDistribution;
    const queueYears = sampleTriangular(dist.p10Years, dist.medianYears, dist.p90Years, rng);
    setConstructionMonths(p, queueYears * 12);
    queueDrawApplied = true;
  }

  if (sample.constructionMonths && !queueDrawApplied) {
    const stated = totalConstructionMonths(pathway);
    if (stated > 0) {
      const sampled = logNormalMeanStdev(stated, 0.15 * stated, rng);
      setConstructionMonths(p, sampled);
    }
  }

  if (sample.capexUsd) {
    const stdev = capexOverrunStdevFraction(p.risk.technologyMaturity);
    const multiplier = logNormalMeanStdev(1, stdev, rng);
    scalePathwayCapex(p, multiplier);
  }

  if (sample.fuelPriceCagr) {
    const mean = pathway.opex.fuelPriceCagrPct ?? 0;
    const stdev = pathway.opex.fuelPriceStdev ?? 0;
    p.opex.fuelPriceCagrPct = stdev > 0 ? normalMeanStdev(mean, stdev, rng) : mean;
  }

  if (sample.availabilityPct) {
    const stated = pathway.revenue.availabilityPct;
    const { alpha, beta } = availabilityBetaParams(stated);
    const sampledFrac = sampleBeta(alpha, beta, rng);
    const sampledPct = clampSampledAvailabilityPct(sampledFrac * 100, stated);
    applySampledAvailability(p, sampledPct);
  }

  return p;
}

function discountedSumByCohort(flows: PathwayCashflow[], rate: number): number {
  let total = 0;
  for (const row of flows) {
    total += row.netCashFlow / Math.pow(1 + rate, row.cohortYear);
  }
  return total;
}

/**
 * Project IRR (percent) from net cashflows by cohort year.
 *
 * Returns NaN when the IRR is undefined or the root solve does not converge:
 *  - no cashflows,
 *  - no sign change (all flows same sign => no real IRR exists),
 *  - the bisection bracket [-0.99, 5] does not contain a sign change, or
 *  - neither Newton-Raphson nor bisection lands within tolerance.
 *
 * R-131-FIN-DECISION-D / irr-nonconvergence: previously these paths returned 0,
 * which silently corrupted the Monte Carlo IRR percentile summary (a failed
 * solve masqueraded as a 0% IRR). NaN is returned instead so non-converged
 * samples can be EXCLUDED from percentile aggregation and counted explicitly.
 * This function must never throw — it is called inside the 1000-iteration
 * Monte Carlo loop, and a throw would abort the entire run.
 */
export function computeIrrPctFromFlows(flows: PathwayCashflow[]): number {
  if (flows.length === 0) return NaN;
  const netFlows = flows.map((row) => row.netCashFlow);
  const cohortYears = flows.map((row) => row.cohortYear);
  const hasNegative = netFlows.some((f) => f < 0);
  const hasPositive = netFlows.some((f) => f > 0);
  if (!hasNegative || !hasPositive) return NaN;

  const npvAtRate = (rate: number): number => {
    let total = 0;
    for (let i = 0; i < netFlows.length; i += 1) {
      total += netFlows[i] / Math.pow(1 + rate, cohortYears[i]);
    }
    return total;
  };

  let r = 0.1;
  const maxIters = 100;
  for (let iter = 0; iter < maxIters; iter += 1) {
    const npv = npvAtRate(r);
    if (Math.abs(npv) < 1e-3) return r * 100;
    let derivative = 0;
    for (let i = 0; i < netFlows.length; i += 1) {
      const t = cohortYears[i];
      if (t > 0) derivative += (-t * netFlows[i]) / Math.pow(1 + r, t + 1);
    }
    if (Math.abs(derivative) < 1e-12) break;
    const step = npv / derivative;
    const next = r - step;
    if (Math.abs(step) < 1e-9) return next * 100;
    if (next <= -0.99) r = -0.5;
    else if (next > 100) break;
    else r = next;
  }

  let lo = -0.99;
  let hi = 5;
  let fLo = npvAtRate(lo);
  let fHi = npvAtRate(hi);
  // No sign change across the bracket => no root in [-0.99, 5]; IRR did not
  // converge for this sample. Return NaN (see function docstring) rather than 0.
  if (fLo * fHi > 0) return NaN;
  for (let iter = 0; iter < 200; iter += 1) {
    const mid = 0.5 * (lo + hi);
    const fMid = npvAtRate(mid);
    if (Math.abs(fMid) < 1e-3 || Math.abs(hi - lo) < 1e-9) return mid * 100;
    if (fLo * fMid <= 0) {
      hi = mid;
      fHi = fMid;
    } else {
      lo = mid;
      fLo = fMid;
    }
  }
  // Bisection exhausted its iteration budget without reaching tolerance.
  return NaN;
}

/** NPV of pathway net cashflows at the risk-adjusted WACC (USD). */
export function computePathwayNpvUsd(pathway: Pathway, now?: Date): number {
  const anchor = now ?? new Date(`${pathway.phases[0]?.startDate ?? '2026-01-01'}T00:00:00Z`);
  const flows = buildPathwayCashflows(pathway, anchor);
  if (flows.length === 0) return 0;
  const rate = pathwayDiscountRateFraction(pathway);
  return discountedSumByCohort(flows, rate);
}

/** Project IRR from pathway net cashflows (percent). */
export function computePathwayIrrPct(pathway: Pathway, now?: Date): number {
  const anchor = now ?? new Date(`${pathway.phases[0]?.startDate ?? '2026-01-01'}T00:00:00Z`);
  const flows = buildPathwayCashflows(pathway, anchor);
  return computeIrrPctFromFlows(flows);
}

function resolveNow(pathway: Pathway, opts: MonteCarloOpts): Date {
  if (opts.now) return opts.now;
  const start = pathway.phases.find((phase) => phase.kind === 'construction')?.startDate;
  return new Date(`${start ?? '2026-01-01'}T00:00:00Z`);
}

function evaluatePathway(pathway: Pathway, now: Date): MonteCarloRunMetrics {
  const lcoe = computePathwayLcoe(pathway, { now });
  const flows = buildPathwayCashflows(pathway, now);
  const timeline = derivePathwayTimeline(pathway, now);
  return {
    lcoeRealPerMwhUsd: lcoe.lcoeRealPerMwhUsd,
    npvUsd: discountedSumByCohort(flows, pathwayDiscountRateFraction(pathway)),
    irrPct: computeIrrPctFromFlows(flows),
    codYear: timeline.codYear,
  };
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const weight = idx - lo;
  return sorted[lo] * (1 - weight) + sorted[hi] * weight;
}

function summarize(values: number[]): MonteCarloPercentileSummary {
  // Exclude non-finite samples (NaN/Infinity) so non-converged IRR draws do not
  // distort the percentiles. If every sample is non-finite, surface NaN
  // percentiles (rather than a misleading 0) so callers can detect total
  // non-convergence. R-131-FIN-DECISION-D / irr-nonconvergence.
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) {
    return { p10: NaN, median: NaN, p90: NaN };
  }
  const sorted = finite.sort((a, b) => a - b);
  return {
    p10: percentile(sorted, 10),
    median: percentile(sorted, 50),
    p90: percentile(sorted, 90),
  };
}

/**
 * Run Monte Carlo over pathway timing, CAPEX, fuel CAGR, and availability.
 * Each draw rebuilds cashflows and returns LCOE / NPV / IRR / COD year.
 */
export function runPathwayMonteCarlo(
  pathway: Pathway,
  opts: MonteCarloOpts,
): MonteCarloResult {
  const runs = opts.runs > 0 ? opts.runs : 1000;
  const now = resolveNow(pathway, opts);
  const rng = createSeededRng(opts.seedRng ?? 1);
  const perRun: MonteCarloRunMetrics[] = [];

  const hasSampling = Object.values(opts.sample).some(Boolean);

  for (let i = 0; i < runs; i += 1) {
    const sampled = hasSampling ? samplePathway(pathway, opts.sample, rng) : clonePathway(pathway);
    perRun.push(evaluatePathway(sampled, now));
  }

  const lcoeValues = perRun.map((row) => row.lcoeRealPerMwhUsd);
  const npvValues = perRun.map((row) => row.npvUsd);
  const irrValues = perRun.map((row) => row.irrPct);
  const codValues = perRun.map((row) => row.codYear);
  const positiveNpv = perRun.filter((row) => row.npvUsd > 0).length;
  // Count IRR samples that did not converge (NaN). summarize() already excludes
  // these from the percentile aggregation; we report the count/fraction so the
  // non-convergence is visible rather than silently folded into the summary.
  const irrNonConvergedCount = irrValues.filter((v) => !Number.isFinite(v)).length;

  return {
    perRun,
    summary: {
      lcoe: summarize(lcoeValues),
      npv: summarize(npvValues),
      irr: summarize(irrValues),
      codYear: summarize(codValues),
      pPositiveNpv: runs > 0 ? positiveNpv / runs : 0,
      irrNonConvergedCount,
      irrNonConvergedFraction: runs > 0 ? irrNonConvergedCount / runs : 0,
    },
  };
}

/** Format p10–median–p90 for compact UI display. */
export function formatMonteCarloRange(
  summary: MonteCarloPercentileSummary,
  formatter: (value: number) => string,
): string {
  return `${formatter(summary.median)} (${formatter(summary.p10)}–${formatter(summary.p90)})`;
}

// ---------------------------------------------------------------------------
// R-131-FIN-DECISION-D / correlated-mc — CORRELATED multi-pathway Monte Carlo.
//
// The per-pathway runPathwayMonteCarlo above draws every factor independently,
// so cross-pathway comparisons ("which is cheapest?") ignore COMMON-MODE risk:
// in reality a supply-chain inflation spike, a fuel-price regime shift, or an
// interconnection-queue slowdown hits EVERY candidate build in the same year,
// not one at a time. runComparisonMonteCarlo (below) draws the global/common
// factors ONCE per iteration and applies that SAME percentile shock to ALL
// pathways, while each pathway's idiosyncratic factors still vary independently.
//
// IMPORTANT: this is purely additive. runPathwayMonteCarlo,
// DEFAULT_COMPARE_MONTE_CARLO_OPTS, samplePathway, and every existing preset's
// numeric output are unchanged — the correlated runner uses its OWN seeded RNG
// streams (createSeededRng) and never touches the existing single-source path.
// ---------------------------------------------------------------------------

/**
 * Per-iteration COMMON-MODE shocks, expressed as DIMENSIONLESS standardised
 * draws so they can be mapped through each pathway's own (already-sourced)
 * distribution parameters. Drawing the standardised shock once and mapping it
 * per-pathway is what makes the shock correlated: a +1.5σ capex-inflation year
 * is a +1.5σ year for every build simultaneously.
 *
 *  - capexInflationZ   — standard-normal z applied to each pathway's
 *                        log-normal CAPEX overrun multiplier (σ from technology
 *                        maturity, capexOverrunStdevFraction). Unit: σ (–).
 *  - fuelPriceZ        — standard-normal z applied to each pathway's fuel-price
 *                        CAGR draw (mean opex.fuelPriceCagrPct, σ opex.fuelPriceStdev).
 *                        Unit: σ (–).
 *  - queueShockU       — uniform(0,1) common quantile applied to each grid
 *                        pathway's triangular queue-delay distribution. The
 *                        SAME quantile means a "P80 bad queue year" delays every
 *                        grid pathway together. Unit: quantile (–).
 */
interface CommonModeDraw {
  capexInflationZ: number;
  fuelPriceZ: number;
  queueShockU: number;
}

/** Standard-normal draw via Box–Muller (shares normalStandard's transform). */
function drawCommonMode(rng: () => number): CommonModeDraw {
  return {
    capexInflationZ: normalStandard(rng),
    fuelPriceZ: normalStandard(rng),
    queueShockU: rng(),
  };
}

/** Map a standard-normal z to a log-normal multiplier with unit mean and given σ. */
function logNormalMultiplierFromZ(stdevFraction: number, z: number): number {
  // Matches logNormalMeanStdev(1, stdevFraction, ·): mean=1 ⇒ mu = -σ²/2.
  if (stdevFraction <= 0) return 1;
  const variance = stdevFraction * stdevFraction;
  const sigma = Math.sqrt(Math.log(1 + variance));
  const mu = -0.5 * sigma * sigma;
  return Math.exp(mu + sigma * z);
}

/** Invert a triangular CDF at quantile u ∈ [0,1] (lo ≤ peak ≤ hi). */
function triangularQuantile(min: number, mode: number, max: number, u: number): number {
  if (min >= max) return mode;
  const lo = Math.min(min, mode, max);
  const hi = Math.max(min, mode, max);
  const peak = clamp(mode, lo, hi);
  const fc = (peak - lo) / (hi - lo);
  if (u < fc) {
    return lo + Math.sqrt(u * (hi - lo) * (peak - lo));
  }
  return hi - Math.sqrt((1 - u) * (hi - lo) * (hi - peak));
}

/**
 * Build a sampled pathway for the CORRELATED runner.
 *
 * Common-mode factors (capex inflation, fuel-price CAGR, queue/schedule shock)
 * are driven by the shared `common` draw so all pathways move together. The
 * remaining (idiosyncratic) factors — construction-month execution scatter and
 * availability — are drawn from the per-pathway `idioRng` so they still vary
 * independently. Mirrors samplePathway's distribution choices exactly.
 */
function sampleCorrelatedPathway(
  pathway: Pathway,
  sample: MonteCarloOpts['sample'],
  common: CommonModeDraw,
  idioRng: () => number,
): Pathway {
  const p = clonePathway(pathway);

  // --- COMMON: queue/schedule shock (grid pathways) — shared quantile.
  let queueDrawApplied = false;
  if (
    sample.queueYears &&
    p.classification === 'grid' &&
    p.risk.queueDelayDistribution
  ) {
    const dist = p.risk.queueDelayDistribution;
    const queueYears = triangularQuantile(
      dist.p10Years,
      dist.medianYears,
      dist.p90Years,
      common.queueShockU,
    );
    setConstructionMonths(p, queueYears * 12);
    queueDrawApplied = true;
  }

  // --- IDIOSYNCRATIC: construction-month execution scatter (per-pathway).
  // Skipped when the queue draw already set this duration — see the note in
  // `samplePathway`: on a grid pathway the construction phase IS the
  // interconnection wait, so running both overwrote the ISO-sourced queue
  // distribution with a generic lognormal on every run.
  if (sample.constructionMonths && !queueDrawApplied) {
    const stated = totalConstructionMonths(pathway);
    if (stated > 0) {
      const sampled = logNormalMeanStdev(stated, 0.15 * stated, idioRng);
      setConstructionMonths(p, sampled);
    }
  }

  // --- COMMON: CAPEX inflation / overrun — shared z, per-pathway σ.
  if (sample.capexUsd) {
    const stdev = capexOverrunStdevFraction(p.risk.technologyMaturity);
    const multiplier = logNormalMultiplierFromZ(stdev, common.capexInflationZ);
    scalePathwayCapex(p, multiplier);
  }

  // --- COMMON: fuel-price CAGR — shared z, per-pathway mean/σ.
  if (sample.fuelPriceCagr) {
    const mean = pathway.opex.fuelPriceCagrPct ?? 0;
    const stdev = pathway.opex.fuelPriceStdev ?? 0;
    p.opex.fuelPriceCagrPct = stdev > 0 ? mean + stdev * common.fuelPriceZ : mean;
  }

  // --- IDIOSYNCRATIC: availability (per-pathway plant execution).
  if (sample.availabilityPct) {
    const stated = pathway.revenue.availabilityPct;
    const { alpha, beta } = availabilityBetaParams(stated);
    const sampledFrac = sampleBeta(alpha, beta, idioRng);
    const sampledPct = clampSampledAvailabilityPct(sampledFrac * 100, stated);
    applySampledAvailability(p, sampledPct);
  }

  return p;
}

/** Percentile bands + win-probability for one pathway under correlated draws. */
export interface ComparisonPathwayResult {
  pathwayId: string;
  name: string;
  perRun: MonteCarloRunMetrics[];
  summary: MonteCarloResult['summary'];
  /**
   * Fraction of correlated iterations (0..1) in which this pathway had the
   * LOWEST finite LCOE across all pathways. pWinLcoe sums to ~1 across the
   * pathways in the comparison (iterations where no pathway has a finite LCOE
   * are dropped from the denominator — see ComparisonMonteCarloResult).
   */
  pWinLcoe: number;
  /** Fraction of iterations in which this pathway had the HIGHEST finite NPV. */
  pWinNpv: number;
}

export interface ComparisonMonteCarloResult {
  runs: number;
  pathways: ComparisonPathwayResult[];
  /** Iterations counted toward pWinLcoe (≥1 pathway had a finite LCOE). */
  lcoeDecidableRuns: number;
  /** Iterations counted toward pWinNpv (≥1 pathway had a finite NPV). */
  npvDecidableRuns: number;
}

function summarizeMetrics(perRun: MonteCarloRunMetrics[], runs: number): MonteCarloResult['summary'] {
  const irrValues = perRun.map((r) => r.irrPct);
  const positiveNpv = perRun.filter((r) => r.npvUsd > 0).length;
  const irrNonConvergedCount = irrValues.filter((v) => !Number.isFinite(v)).length;
  return {
    lcoe: summarize(perRun.map((r) => r.lcoeRealPerMwhUsd)),
    npv: summarize(perRun.map((r) => r.npvUsd)),
    irr: summarize(irrValues),
    codYear: summarize(perRun.map((r) => r.codYear)),
    pPositiveNpv: runs > 0 ? positiveNpv / runs : 0,
    irrNonConvergedCount,
    irrNonConvergedFraction: runs > 0 ? irrNonConvergedCount / runs : 0,
  };
}

/**
 * CORRELATED multi-pathway Monte Carlo.
 *
 * For each of `opts.runs` iterations a single COMMON-MODE draw (capex
 * inflation, fuel-price CAGR shock, queue/schedule shock) is generated and
 * applied to EVERY pathway, while each pathway's idiosyncratic factors
 * (construction-month scatter, availability) vary independently. This makes the
 * cross-pathway "win" probabilities reflect correlated scenarios — e.g. a
 * supply-chain inflation year that hits all candidate builds at once.
 *
 * Reproducibility: uses ONLY the existing seeded RNG (createSeededRng). The
 * common-mode stream and each pathway's idiosyncratic stream are seeded from
 * `opts.seedRng` and varied by iteration index, so the result is identical for
 * a fixed seed and never reads the platform global random source.
 *
 * Output per pathway: LCOE/NPV/IRR/COD percentile bands under the correlated
 * draws, plus pWinLcoe (probability of being lowest-LCOE) and pWinNpv
 * (probability of being highest-NPV). pWinLcoe sums to ~1 across pathways, as
 * does pWinNpv (over decidable iterations).
 *
 * Existing single-source behaviour is untouched: runPathwayMonteCarlo and
 * DEFAULT_COMPARE_MONTE_CARLO_OPTS are unchanged and this function is opt-in.
 */
export function runComparisonMonteCarlo(
  pathways: Pathway[],
  opts: MonteCarloOpts,
): ComparisonMonteCarloResult {
  const runs = opts.runs > 0 ? opts.runs : 1000;
  const seed = opts.seedRng ?? 1;
  const hasSampling = Object.values(opts.sample).some(Boolean);

  // Per-pathway anchor date + idiosyncratic RNG. Anchors match the single-source
  // path (resolveNow) so deterministic baselines line up. Each pathway gets its
  // OWN idiosyncratic stream (seed offset by index) so idiosyncratic factors are
  // independent across pathways; the common stream is shared.
  const commonRng = createSeededRng(seed);
  const ctx = pathways.map((pathway, idx) => ({
    pathway,
    now: resolveNow(pathway, opts),
    // Distinct, deterministic offset per pathway; large prime stride avoids
    // collisions with the common stream's seed.
    idioRng: createSeededRng((seed + (idx + 1) * 0x9e3779b1) >>> 0),
    perRun: [] as MonteCarloRunMetrics[],
    winLcoe: 0,
    winNpv: 0,
  }));

  let lcoeDecidableRuns = 0;
  let npvDecidableRuns = 0;

  for (let i = 0; i < runs; i += 1) {
    // ONE common-mode draw per iteration, applied to ALL pathways.
    const common = drawCommonMode(commonRng);

    let bestLcoe = Number.POSITIVE_INFINITY;
    let bestLcoeIdx = -1;
    let bestNpv = Number.NEGATIVE_INFINITY;
    let bestNpvIdx = -1;

    for (let j = 0; j < ctx.length; j += 1) {
      const c = ctx[j];
      const sampled = hasSampling
        ? sampleCorrelatedPathway(c.pathway, opts.sample, common, c.idioRng)
        : clonePathway(c.pathway);
      const metrics = evaluatePathway(sampled, c.now);
      c.perRun.push(metrics);

      // P(win on LCOE) is decided on the SAME basis as the reported LCOE, so
      // the pathway the reader sees as cheapest is the one that wins here.
      if (Number.isFinite(metrics.lcoeRealPerMwhUsd) && metrics.lcoeRealPerMwhUsd < bestLcoe) {
        bestLcoe = metrics.lcoeRealPerMwhUsd;
        bestLcoeIdx = j;
      }
      if (Number.isFinite(metrics.npvUsd) && metrics.npvUsd > bestNpv) {
        bestNpv = metrics.npvUsd;
        bestNpvIdx = j;
      }
    }

    if (bestLcoeIdx >= 0) {
      ctx[bestLcoeIdx].winLcoe += 1;
      lcoeDecidableRuns += 1;
    }
    if (bestNpvIdx >= 0) {
      ctx[bestNpvIdx].winNpv += 1;
      npvDecidableRuns += 1;
    }
  }

  return {
    runs,
    lcoeDecidableRuns,
    npvDecidableRuns,
    pathways: ctx.map((c) => ({
      pathwayId: c.pathway.id,
      name: c.pathway.name,
      perRun: c.perRun,
      summary: summarizeMetrics(c.perRun, runs),
      // Normalise over DECIDABLE iterations so the probabilities sum to ~1
      // across pathways (an iteration where no pathway has a finite LCOE/NPV is
      // excluded from both numerator and denominator).
      pWinLcoe: lcoeDecidableRuns > 0 ? c.winLcoe / lcoeDecidableRuns : 0,
      pWinNpv: npvDecidableRuns > 0 ? c.winNpv / npvDecidableRuns : 0,
    })),
  };
}
